import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  ArrowTopRightOnSquareIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';
import SEO from '../components/SEO';
import { canonicalUrlFor, metaDescription } from '../utils/seo';
import { opportunityAPI } from '../services/api';
import { OpportunityDetail as OpportunityDetailType } from '../types';
import { formatDate, formatDueDateTime, formatDueSummary, formatDueTime } from '../utils/contractFormatting';
import { getProcuringTypeMeta } from '../utils/procuringTypes';
import { DEFAULT_REGION } from '../regions';
import OpportunityTypeBadge from '../components/OpportunityTypeBadge';
import SaveOpportunityButton from '../components/SaveOpportunityButton';
import { trackEvent } from '../utils/analytics';
import { describeOpportunityType } from '../utils/opportunityTypes';
import MatchReasons from '../components/MatchReasons';
import { getOpportunitySourceAriaLabel, getOpportunitySourceName } from '../utils/opportunitySources';

const UNSPSC_CODE_PATTERN = /\b\d{2}-\d{2}-\d{2}(?:-\d{2})?(?:-\d{4})?\b/g;

function formatUnspscDisplay(description?: string | null, code?: string | null): string | null {
  const normalizedDescription = description?.replace(/\s+/g, ' ').trim() || '';
  const normalizedCode = code?.trim() || '';

  const codes = Array.from(normalizedDescription.matchAll(UNSPSC_CODE_PATTERN)).map((match) => match[0]);
  const parts = normalizedDescription.split(UNSPSC_CODE_PATTERN).map((part) => part.trim());

  let selectedCode = normalizedCode;
  let selectedDescription = normalizedDescription;

  if (codes.length > 0) {
    const mostSpecificIndex = codes.reduce((bestIndex, candidate, index) => {
      const best = codes[bestIndex];
      if (candidate.length > best.length) return index;
      if (candidate.length === best.length) return index;
      return bestIndex;
    }, 0);

    selectedCode = codes[mostSpecificIndex];
    selectedDescription = parts[mostSpecificIndex + 1] || parts[mostSpecificIndex] || normalizedDescription;
  }

  if (!selectedDescription && selectedCode) {
    return `UNSPSC Code: ${selectedCode}`;
  }

  if (selectedDescription && selectedCode) {
    return `${selectedDescription} (UNSPSC Code: ${selectedCode})`;
  }

  return selectedDescription || null;
}

const OpportunityDetail: React.FC = () => {
  const location = useLocation();
  const { id, region } = useParams();
  const targetRegion = region ?? DEFAULT_REGION;
  const browsePath = `/${targetRegion}/opportunities${location.search}`;
  const [opportunity, setOpportunity] = useState<OpportunityDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const numericId = useMemo(() => {
    if (!id) return null;
    const parsed = Number(id);
    return Number.isFinite(parsed) ? parsed : null;
  }, [id]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [numericId]);

  useEffect(() => {
    let isActive = true;
    const loadDetail = async () => {
      if (!numericId) {
        setError('Invalid opportunity ID.');
        setLoading(false);
        return;
      }
      try {
        const response = await opportunityAPI.getOpportunityDetail(numericId);
        if (!isActive) return;
        if (!response.data.success) {
          setError('Opportunity not found.');
        } else {
          setOpportunity(response.data.opportunity);
        }
      } catch {
        if (!isActive) return;
        setError('Unable to load this opportunity right now.');
      } finally {
        if (isActive) setLoading(false);
      }
    };
    loadDetail();
    return () => {
      isActive = false;
    };
  }, [numericId]);

  if (loading) {
    return <LoadingSpinner message="Loading opportunity..." />;
  }

  if (error || !opportunity) {
    return (
      <div className="space-y-6">
        <SEO
          title="Opportunity – OppFoundry"
          description={metaDescription('View opportunity details and attachments.')}
          canonical={canonicalUrlFor()}
        />
        <Link
          to={browsePath}
          className="text-sm font-semibold text-blue-600 hover:text-blue-700"
        >
          ← Back to opportunities
        </Link>
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-600 shadow-sm">
          {error ?? 'Opportunity not found.'}
        </div>
      </div>
    );
  }

  const procuringOrg = opportunity.procuring_organization || opportunity.city;
  const procuringMeta = getProcuringTypeMeta(opportunity.procuring_type);
  const detailUrl = opportunity.bid_page_url || opportunity.source_url;
  const attachments = opportunity.attachments ?? [];
  const dueSummary = formatDueSummary(opportunity);
  const dueTime = formatDueTime(opportunity.due_at);
  const hasPreBidMeeting = Boolean(opportunity.pre_bid_meeting?.trim());
  const unspscDisplay = formatUnspscDisplay(opportunity.unspsc_description, opportunity.unspsc_code);

  return (
    <div className="space-y-8">
      <SEO
        title={`${opportunity.title} – OppFoundry`}
        description={metaDescription(opportunity.title)}
        canonical={canonicalUrlFor()}
      />

      <Link
        to={browsePath}
        className="text-sm font-semibold text-blue-600 hover:text-blue-700"
      >
        ← Back to opportunities
      </Link>

      <div className="grid items-start gap-6 lg:grid-cols-3">
        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2">
          <div className="flex items-start gap-4">
            <h1 className="min-w-0 flex-1 break-words text-3xl font-semibold text-slate-900">{opportunity.title}</h1>
            <div className="flex shrink-0 items-center gap-3">
              <SaveOpportunityButton opportunity={opportunity} surface="detail" />
              {detailUrl && (
                <a
                  href={detailUrl}
                  onClick={() => trackEvent('saved_opportunity_source_clicked', { contract_id: opportunity.id, surface: 'detail' })}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  aria-label={getOpportunitySourceAriaLabel(opportunity.source_type)}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-600 transition hover:border-blue-200 hover:bg-blue-100"
                >
                  {getOpportunitySourceName(opportunity.source_type)}
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
              )}
              {opportunity.source_url && opportunity.source_url !== detailUrl && (
                <a
                  href={opportunity.source_url}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  aria-label={getOpportunitySourceAriaLabel(opportunity.source_type, 'listing')}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
                >
                  {getOpportunitySourceName(opportunity.source_type)} listing
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>

          {opportunity.opportunity_type && (
            <div className="mt-4 flex items-center gap-3">
              <OpportunityTypeBadge value={opportunity.opportunity_type} />
              <span className="text-sm text-slate-500">
                {describeOpportunityType(opportunity.opportunity_type)}
              </span>
            </div>
          )}

          <MatchReasons reasons={opportunity.match_reasons} variant="panel" className="mt-5" />

          <div className="mt-6 space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <div className="grid gap-6 lg:grid-cols-2">
                <div className="space-y-4">
                  <DetailRowBlock
                    label="Industry"
                    value={
                      <div className="space-y-2">
                        <div>{opportunity.industry || 'Not provided'}</div>
                        {unspscDisplay && (
                          <div className="border-l border-slate-200 pl-3 text-xs leading-relaxed text-slate-500">
                            <span>{unspscDisplay}</span>
                          </div>
                        )}
                      </div>
                    }
                  />
                  <DetailRowBlock label="Procuring Org" value={procuringOrg} />
                  <DetailRowBlock
                    label="Procuring Org Type"
                    value={
                      procuringMeta?.label ? (
                        <span className="inline-flex items-center gap-2">
                          <img src={procuringMeta.icon} alt={procuringMeta.label} className="h-4 w-4 shrink-0 object-contain" />
                          <span>{procuringMeta.label}</span>
                        </span>
                      ) : (
                        'Not provided'
                      )
                    }
                  />
                </div>
                <div className="space-y-4">
                  <DetailRowBlock label="Release Date" value={formatDate(opportunity.release_date)} />
                  <DetailRowBlock
                    label="Deadline"
                    value={
                      <div className="space-y-1">
                        <div>{formatDueDateTime(opportunity)}</div>
                        {dueSummary && <div className="text-xs text-slate-500">{dueSummary}</div>}
                        {!dueTime && opportunity.due_date !== 'TBD' && (
                          <div className="text-xs text-slate-500">Time not specified</div>
                        )}
                      </div>
                    }
                  />
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Description</h2>
              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-700">
                {opportunity.description || 'No description provided.'}
              </p>
            </div>
          </div>

          {hasPreBidMeeting && (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <h2 className="text-sm font-semibold text-slate-800">Pre-Bid Meeting</h2>
              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-700">
                {opportunity.pre_bid_meeting}
              </p>
            </div>
          )}

          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <h2 className="text-sm font-semibold text-slate-800">Attachments</h2>
            {attachments.length ? (
              <ul className="mt-3 space-y-2 text-sm text-slate-700">
                {attachments.map((attachment, index) => (
                  <li key={`${attachment.url}-${index}`}>
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700"
                    >
                      <DocumentTextIcon className="h-4 w-4" />
                      <span>{attachment.title}</span>
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-slate-500">No attachments available.</p>
            )}
          </div>
        </section>

        <aside className="space-y-6">
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Procurer Details</h2>
            <div className="mt-4 space-y-4">
              <DetailRowBlock label="Procuring Org" value={procuringOrg} />
              <DetailRowBlock label="Procuring Department" value={opportunity.department} />
              <DetailRowBlock label="Buyer" value={opportunity.buyer} />
              <DetailRowBlock label="Contact for more Information:" value={opportunity.info_contact} />
              <DetailRowBlock label="State" value={opportunity.state || 'MA'} />
              <DetailRowBlock label="Bid Number" value={opportunity.bid_number} />
              <DetailRowBlock label="Alternate ID" value={opportunity.alternate_id} />
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Submission Details</h2>
            <div className="mt-4 space-y-4">
              <DetailRowBlock label="COMMBUYS Submission" value={opportunity.metadata_allow_equote} />
              <DetailRowBlock label="Proposal Submission" value={opportunity.metadata_ship_to} />
              <DetailRowBlock label="Invoice Submission" value={opportunity.metadata_bill_to} />
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
};

const DetailRowBlock: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="space-y-1">
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    <div className="whitespace-pre-line text-sm text-slate-700">{value || 'Not provided'}</div>
  </div>
);

export default OpportunityDetail;
